import { useState } from "react";
import axios from "axios";
import { toast, Toaster } from "sonner";
import { Send, Phone, Mail, CheckCircle2 } from "lucide-react";
import { SITE } from "@/data/siteData";
import { CITIES } from "@/data/cities";
import { SERVICES } from "@/data/services";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function QuoteForm({ preselectedCity, preselectedService }) {
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    company: "",
    city: preselectedCity || "",
    service: preselectedService || "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error("Merci d'indiquer votre nom et téléphone.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/quote-requests`, {
        ...form,
        source_page: typeof window !== "undefined" ? window.location.pathname : "",
      });
      setDone(true);
      toast.success("Demande envoyée ! Nous vous rappelons rapidement.");
      setForm({ name: "", phone: "", email: "", company: "", city: "", service: "", message: "" });
    } catch (err) {
      toast.error("Une erreur est survenue. Appelez-nous au " + SITE.phoneDisplay);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="devis" data-testid="quote-section" className="bg-gradient-to-b from-white to-[#f8fafc] py-20 md:py-28">
      <Toaster richColors position="top-right" />
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-sm uppercase tracking-[0.25em] text-[#066ec4] font-bold mb-4">Devis gratuit</div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-[#00367e] leading-[1.05]">
            Recevez votre <span className="dh-underline">devis</span> sous 1h
          </h2>
          <p className="mt-5 text-lg text-slate-700">
            Décrivez votre besoin, nous vous rappelons sous 1h ouvrée avec un tarif clair et transparent.
          </p>
          <div className="mt-8 space-y-4">
            <a href={`tel:${SITE.phoneTel}`} className="flex items-center gap-3 text-[#00367e] font-bold text-lg hover:text-[#066ec4]" data-testid="quote-phone-link">
              <span className="w-12 h-12 rounded-full bg-[#00367e] text-white flex items-center justify-center"><Phone size={20} /></span>
              {SITE.phoneDisplay}
            </a>
            <a href={`mailto:${SITE.email}`} className="flex items-center gap-3 text-[#00367e] font-bold hover:text-[#066ec4]" data-testid="quote-email-link">
              <span className="w-12 h-12 rounded-full bg-[#066ec4] text-white flex items-center justify-center"><Mail size={20} /></span>
              {SITE.email}
            </a>
          </div>
          <div className="mt-8 p-5 rounded-2xl bg-[#f1f5fb] border border-slate-100">
            <div className="font-bold text-[#00367e]">Urgence ?</div>
            <p className="text-slate-700 text-sm mt-1">
              Nous intervenons 7j/7 - 24h/24 pour les sinistres, contrôles d'hygiène, sécurité incendie ou réouvertures rapides.
            </p>
          </div>
        </div>

        <div className="lg:col-span-7">
          {done ? (
            <div data-testid="quote-success" className="bg-white rounded-3xl p-10 border border-slate-100 shadow-[0_20px_50px_rgba(0,54,126,0.10)] text-center">
              <div className="w-20 h-20 mx-auto rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 size={48} />
              </div>
              <h3 className="mt-6 text-2xl font-bold text-[#00367e]">Demande envoyée !</h3>
              <p className="mt-3 text-slate-700">
                Nous vous rappelons très rapidement au numéro indiqué. Pour une réponse immédiate, appelez le {SITE.phoneDisplay}.
              </p>
              <button
                onClick={() => setDone(false)}
                className="mt-6 px-6 py-3 rounded-full border-2 border-[#00367e] text-[#00367e] font-bold hover:bg-[#00367e] hover:text-white transition-colors"
              >
                Nouvelle demande
              </button>
            </div>
          ) : (
            <form
              onSubmit={submit}
              data-testid="quote-form"
              className="bg-white rounded-3xl p-7 md:p-9 border border-slate-100 shadow-[0_20px_50px_rgba(0,54,126,0.10)]"
            >
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Nom complet *" data-testid="quote-name" type="text" required value={form.name} onChange={onChange("name")} />
                <Field label="Téléphone *" data-testid="quote-phone" type="tel" required value={form.phone} onChange={onChange("phone")} />
                <Field label="Email" data-testid="quote-email" type="email" value={form.email} onChange={onChange("email")} />
                <Field label="Établissement / Restaurant" data-testid="quote-company" type="text" value={form.company} onChange={onChange("company")} />
                <Select
                  label="Ville"
                  testid="quote-city"
                  value={form.city}
                  onChange={onChange("city")}
                  options={[{ value: "", label: "Sélectionner..." }, ...CITIES.map(c => ({ value: c.name, label: `${c.name} (${c.postal})` }))]}
                />
                <Select
                  label="Service souhaité"
                  testid="quote-service"
                  value={form.service}
                  onChange={onChange("service")}
                  options={[{ value: "", label: "Sélectionner..." }, ...SERVICES.map(s => ({ value: s.title, label: s.title }))]}
                />
              </div>
              <div className="mt-4">
                <label className="block text-sm font-semibold text-[#00367e] mb-2">Décrivez votre besoin</label>
                <textarea
                  data-testid="quote-message"
                  value={form.message}
                  onChange={onChange("message")}
                  rows={4}
                  placeholder="Type de cuisine, taille de la hotte, fréquence souhaitée..."
                  className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3 text-[#0a0a0a] focus:ring-2 focus:ring-[#066ec4] focus:border-transparent transition-all outline-none resize-none"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                data-testid="quote-submit-btn"
                className="dh-cta-shine mt-6 w-full inline-flex items-center justify-center gap-2 px-7 py-5 rounded-full bg-[#00367e] hover:bg-[#066ec4] text-white font-extrabold text-lg shadow-xl transition-all disabled:opacity-60"
              >
                <Send size={20} /> {loading ? "Envoi en cours..." : "Envoyer ma demande"}
              </button>
              <p className="mt-4 text-xs text-slate-500 text-center">
                En envoyant ce formulaire, vous acceptez d'être recontacté par {SITE.name}. Aucune donnée n'est partagée à des tiers.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

const Field = ({ label, ...props }) => (
  <div>
    <label className="block text-sm font-semibold text-[#00367e] mb-2">{label}</label>
    <input
      {...props}
      className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3 text-[#0a0a0a] focus:ring-2 focus:ring-[#066ec4] focus:border-transparent transition-all outline-none"
    />
  </div>
);

const Select = ({ label, testid, value, onChange, options }) => (
  <div>
    <label className="block text-sm font-semibold text-[#00367e] mb-2">{label}</label>
    <select
      data-testid={testid}
      value={value}
      onChange={onChange}
      className="w-full bg-[#f8fafc] border border-slate-200 rounded-xl px-4 py-3 text-[#0a0a0a] focus:ring-2 focus:ring-[#066ec4] focus:border-transparent transition-all outline-none"
    >
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  </div>
);
